import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from "react";
import { STORAGE_KEY, useStorageContext } from "./StorageContext";

export interface Preferences {
  theme?: "light" | "dark";
  sidebarCollapsed?: boolean;
  /** Active tenant id (header tenant switcher). */
  tenant?: string;
  vehicleUnits?: "metric" | "imperial";
  dockDefault?: "bottom" | "right";
  /** OR'd with the prefers-reduced-motion media query. */
  reduceMotion?: boolean;
}

interface Context {
  preferences?: Preferences;
  setPreference?: <T extends keyof Preferences>(
    key: T,
    value: Preferences[T]
  ) => void;
}

export const PreferenceContext = React.createContext<Context>({});
export function usePreferences(): Context {
  return useContext(PreferenceContext);
}

/**
 * Storage provider to provide storage adapters
 * @param props
 * @returns
 */
export const PreferenceProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const { storage } = useStorageContext();

  const [preferences, setPreferences] = useState<Preferences>(() =>
    storage.get(STORAGE_KEY.PREFS, {})
  );

  // keep preferences
  useEffect(() => {
    if (Object.keys(preferences).length) {
      storage.set(STORAGE_KEY.PREFS, preferences);
    }
  }, [storage, preferences]);

  const setPreference = useCallback(
    <T extends keyof Preferences>(key: T, value: Preferences[T]) => {
      setPreferences((prev) => ({ ...prev, [key]: value }));
    },
    [setPreferences]
  );
  return (
    <PreferenceContext.Provider
      value={{
        preferences,
        setPreference,
      }}
    >
      {children}
    </PreferenceContext.Provider>
  );
};
